/*
 * Eataly — mobile add-to-cart confirmation.
 *
 * The theme's Cart component reacts to `product:added` by scrolling the page
 * back to the top and opening the legacy mini-cart drawer. On a phone that
 * throws the shopper out of the grid they were browsing. This module replaces
 * that with a compact bottom sheet on mobile only.
 *
 * How it gets in front of the theme: `Cart._attachListeners` registers its
 * `product:added` handler through Delegate on `document.documentElement`, and
 * `Delegate.captureForType` returns false for this event type, so the theme
 * listens in the bubble phase. A capture-phase listener on `window` therefore
 * always runs first and can stop the event before the theme sees it.
 *
 * What still goes through the theme: after stopping the event we dispatch
 * `cart:refresh`, which is the theme's own public refresh path. That updates the
 * header count and the mini-cart contents from the server and ends the loading
 * bar, so the cart icon keeps working normally.
 *
 * Which product a confirmation is about:
 *
 *   `product:added` only carries a real `variant` on product-page adds; every
 *   card, quick-buy, recommendation and recently-viewed path dispatches
 *   `variant: null` from the *section* element. So the six native `_addToCart`
 *   handlers in assets/theme.js and assets/theme.min.js were patched to put
 *   `addedFrom: EatalyAddConfirmation.snapshot(target)` in the detail of the
 *   success event — metadata resolved from the button that started that one
 *   request, captured when it was clicked (see `snapshot` below).
 *
 *   The confirmation therefore reads the product out of the event it is
 *   handling. There is no queue and no shared "most recent add" state, so an
 *   add that fails cannot lend its product to the next one, and two adds that
 *   complete out of order cannot be swapped.
 *
 * Failures never reach here: every theme add path dispatches `product:added`
 * only inside `if (response.ok)`. GrocerBox's own cart writes dispatch
 * `cart:refresh`, not `product:added`, so they cannot trigger a confirmation.
 *
 * The pure helpers are exported for tests/eataly-add-confirmation.test.js.
 */
(function (root, factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    root.EatalyAddConfirmation = api;
    api.init(root);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  var MOBILE_QUERY = '(max-width: 640px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  var AUTO_DISMISS_MS = 6000;

  /*
   * The sheet pops in and pops out, so `hidden` cannot be applied until the exit
   * transition has finished. `transitionend` is the primary signal; EXIT_MS has
   * to match the .is-leaving duration in eataly-add-confirmation.css (there is a
   * test that compares the two), and the fallback allows for the frame the
   * transition takes to start plus the case where no transition runs at all —
   * above 640px the container is display:none, and a display:none subtree fires
   * no transitionend.
   */
  var EXIT_MS = 200;
  var EXIT_FALLBACK_MS = EXIT_MS + 120;
  var DEFAULT_VARIANT_TITLE = 'Default Title';
  var THUMBNAIL_WIDTH = 200;
  var SNAPSHOT_KEY = '__eatalyAddedFrom';

  // Shopify's theme editor and the shop preview each use their own bottom bar.
  var PREVIEW_BAR_IDS = ['PBarNextFrame', 'preview-bar-iframe'];

  /* ---------------------------------------------------------------------- *
   * Pure helpers
   * ---------------------------------------------------------------------- */

  function trimmed(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function positiveQuantity(value) {
    var parsed = parseInt(value, 10);
    return isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  // "Default Title" is Shopify's placeholder for single-variant products and
  // means nothing to a shopper, so it is never shown.
  function variantLabel(value) {
    var label = trimmed(value);
    return label === DEFAULT_VARIANT_TITLE ? '' : label;
  }

  // Card images are lazyloaded from a `..._{width}x.jpg` template.
  function thumbnail(url) {
    var src = trimmed(url);
    return src.indexOf('{width}') === -1 ? src : src.replace('{width}', String(THUMBNAIL_WIDTH));
  }

  /*
   * Decide whether a successful add should be confirmed with the sheet.
   * Anything that returns false is left entirely to the theme.
   */
  function shouldConfirm(state) {
    var current = state || {};

    return !!(
      current.isMobile &&
      current.pageType !== 'cart' &&
      positiveQuantity(current.quantity)
    );
  }

  /*
   * What the sheet says about one add, taken only from that add's own event.
   *
   * `detail.addedFrom` is the per-request metadata the patched native handlers
   * attach; `detail.variant` is a real variant object on product-page adds and
   * null everywhere else. Nothing is read from the cart or from any earlier
   * add, so a confirmation cannot name the wrong product.
   */
  function describeAdd(detail) {
    var payload = detail || {};
    var quantity = positiveQuantity(payload.quantity);

    if (!quantity) {
      return null;
    }

    var context = payload.addedFrom || {};
    var variant = payload.variant || null;
    var variantImage = variant && variant.featured_image ? variant.featured_image.src : '';
    var label = variantLabel(variant ? variant.public_title || variant.title : context.variant);
    var title = trimmed(context.title) || trimmed(variant && variant.name);
    var meta = ['Qty ' + quantity];

    if (label) {
      meta.unshift(label);
    }

    var announcement = 'Added to your cart';

    if (title) {
      announcement += ': ' + (quantity > 1 ? quantity + ' × ' : '') + title;

      if (label) {
        announcement += ' (' + label + ')';
      }
    } else if (quantity > 1) {
      announcement += ': ' + quantity + ' items';
    }

    return {
      title: title,
      variant: label,
      quantity: quantity,
      image: thumbnail(variantImage) || thumbnail(context.image),
      meta: meta.join(' · '),
      announcement: announcement,
      opener: context.element || null
    };
  }

  /* ---------------------------------------------------------------------- *
   * Per-request metadata
   * ---------------------------------------------------------------------- */

  function textOf(element) {
    return element ? trimmed(element.textContent) : '';
  }

  // Lazyloaded images only have their real `src` once they are in view.
  function imageOf(element) {
    if (!element) {
      return '';
    }

    return element.currentSrc || element.getAttribute('src') || element.getAttribute('data-src') || '';
  }

  function readSnapshot(element) {
    var card = element.closest('.product-item');

    if (card) {
      return {
        title: textOf(card.querySelector('.product-item__title')),
        image: imageOf(card.querySelector('.product-item__primary-image')),
        variant: ''
      };
    }

    var productSection = element.closest('[data-section-type="product"]');

    if (productSection) {
      return {
        title: textOf(productSection.querySelector('.product-meta__title')),
        image: imageOf(productSection.querySelector('.product-gallery__image')),
        variant: ''
      };
    }

    return { title: '', image: '', variant: '' };
  }

  /*
   * Metadata for the add that this button is starting. Called by the patched
   * native `_addToCart` handlers with their own `target`, so the result belongs
   * to that request and to no other.
   *
   * The reading happens on the click (the capture-phase listener below runs
   * before the theme's delegated handler issues the request) and is cached on
   * the button, so a card that gets re-rendered while the request is in flight
   * is still described by what the shopper actually clicked.
   */
  function snapshot(element) {
    if (!element || typeof element.closest !== 'function') {
      return null;
    }

    var cached = element[SNAPSHOT_KEY];

    if (!cached) {
      cached = readSnapshot(element);
      element[SNAPSHOT_KEY] = cached;
    }

    return {
      title: cached.title,
      image: cached.image,
      variant: cached.variant,
      element: element
    };
  }

  /* ---------------------------------------------------------------------- *
   * DOM
   * ---------------------------------------------------------------------- */

  var SHEET_MARKUP = [
    '<div class="eataly-added__sheet" role="group" aria-label="Added to your cart">',
    '<p class="visually-hidden" role="status" aria-live="polite" data-added-announcement></p>',
    '<div class="eataly-added__item">',
    '<img class="eataly-added__image" data-added-image alt="" width="56" height="56">',
    '<div class="eataly-added__copy">',
    '<p class="eataly-added__headline">Added to your cart</p>',
    '<p class="eataly-added__title" data-added-title></p>',
    '<p class="eataly-added__meta" data-added-meta></p>',
    '</div>',
    '<button type="button" class="eataly-added__close" data-added-close aria-label="Close">',
    '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none"></path></svg>',
    '</button>',
    '</div>',
    '<div class="eataly-added__actions">',
    '<a class="eataly-added__action button button--primary" data-added-view href="/cart">View cart</a>',
    '<button type="button" class="eataly-added__action button button--ternary" data-added-close>Continue shopping</button>',
    '</div>',
    '</div>'
  ].join('');

  // A plain left click / keyboard activation, as opposed to the modifier and
  // middle-click combinations that mean "open this somewhere else".
  function unmodifiedPrimaryClick(event) {
    return (
      (event.button === undefined || event.button === 0) &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    );
  }

  function init(win) {
    var doc = win.document;
    var element = null;
    var parts = null;
    var timer = null; // auto-dismiss
    var exitTimer = null; // fallback for a transitionend that never arrives
    var opener = null;
    var announcement = ''; // what the pending entry frame should announce
    var forwarding = false;

    /*
     * hidden   — down and display:none.
     * entering — un-hidden, but the entry frame has not run, so nothing is drawn.
     * open     — on screen.
     * leaving  — playing the exit; still displayed, but inert and untappable.
     *
     * This is the only thing that decides whether a callback arriving late still
     * applies, which is what stops a dismissed sheet from being reopened by its
     * own entry frame and a cancelled hide from applying `hidden` to a sheet a
     * newer add has already taken back. Entry frames are deliberately not
     * cancelled: one no-op callback is cheaper than a second mechanism that has
     * to agree with this one.
     */
    var state = 'hidden';

    function previewBarVisible() {
      for (var index = 0; index < PREVIEW_BAR_IDS.length; index++) {
        var bar = doc.getElementById(PREVIEW_BAR_IDS[index]);

        if (bar && (bar.offsetHeight > 0 || bar.getBoundingClientRect().height > 0)) {
          return true;
        }
      }

      return false;
    }

    function cancelTimers() {
      win.clearTimeout(timer);
      win.clearTimeout(exitTimer);
      timer = null;
      exitTimer = null;
    }

    // End of the exit: only now may the sheet go display:none.
    function settle() {
      if (state !== 'leaving') {
        return; // a newer add took the sheet back
      }

      win.clearTimeout(exitTimer);
      exitTimer = null;
      state = 'hidden';
      element.classList.remove('is-leaving');
      element.inert = false;
      element.setAttribute('hidden', 'hidden');
    }

    function hide(restoreFocus) {
      if (!element || state === 'hidden' || state === 'leaving') {
        return;
      }

      // Read this before anything moves focus or hides the sheet.
      var hadFocus = element.contains(doc.activeElement);
      var wasDrawn = state === 'open';

      cancelTimers();
      element.classList.remove('is-visible');

      // Dismissing from the close button or Continue shopping would otherwise
      // drop focus to the top of the document; send it back to the add button
      // the shopper used. Done before the sheet goes inert, so it is never left
      // holding a focused control the shopper can no longer reach.
      if (restoreFocus && hadFocus && opener && doc.documentElement.contains(opener) && opener.focus) {
        opener.focus();
      }

      // Nothing to animate out when nothing was drawn: Escape or the close button
      // landing between removeAttribute('hidden') and the entry frame, or a
      // shopper who asked for no motion.
      if (!wasDrawn || win.matchMedia(REDUCED_MOTION_QUERY).matches) {
        state = 'hidden';
        element.setAttribute('hidden', 'hidden');
        return;
      }

      state = 'leaving';
      element.classList.add('is-leaving');

      // Untappable via CSS, unfocusable via inert (a no-op property assignment
      // where inert is unsupported, which only leaves a ~200ms tab-in window).
      element.inert = true;

      exitTimer = win.setTimeout(settle, EXIT_FALLBACK_MS);
    }

    function build() {
      element = doc.createElement('div');
      element.className = 'eataly-added';
      element.setAttribute('hidden', 'hidden');
      element.innerHTML = SHEET_MARKUP;

      parts = {
        sheet: element.querySelector('.eataly-added__sheet'),
        announcement: element.querySelector('[data-added-announcement]'),
        image: element.querySelector('[data-added-image]'),
        title: element.querySelector('[data-added-title]'),
        meta: element.querySelector('[data-added-meta]'),
        view: element.querySelector('[data-added-view]')
      };

      parts.view.setAttribute('href', (win.routes && win.routes.cartUrl) || '/cart');

      /*
       * View cart navigates itself rather than leaving it to the anchor's
       * default action, because the auto-dismiss can expire in the same tick as
       * the tap: `hidden`/`inert` landing between pointerdown and the default
       * navigation is a click that does nothing. The href stays on the anchor so
       * middle-click, cmd-click and "open in new tab" are unaffected (those are
       * let through below), and the sheet is deliberately neither hidden nor
       * made inert here — tearing it down before the browser commits the
       * navigation is the failure this is avoiding.
       *
       * Registered on the link itself, so it runs before the sheet-level close
       * handler below.
       */
      parts.view.addEventListener('click', function (event) {
        if (state === 'hidden' || state === 'leaving' || !unmodifiedPrimaryClick(event)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        win.clearTimeout(timer);
        timer = null;
        win.location.assign(parts.view.getAttribute('href'));
      });

      element.addEventListener('click', function (event) {
        if (event.target.closest('[data-added-close]')) {
          hide(true);
        }
      });

      // The sheet is not modal and never takes focus, but once a shopper does
      // tab into it, it must not vanish under them.
      element.addEventListener('focusin', function () {
        win.clearTimeout(timer);
      });

      // focusin is not enough on touch: a tap can land in the same tick as the
      // auto-dismiss and lose the race. Capture phase, so the pending dismissal
      // is gone before any click handling runs.
      element.addEventListener('pointerdown', function () {
        win.clearTimeout(timer);
        timer = null;
      }, true);

      element.addEventListener('transitionend', function (event) {
        // The sheet's own fade is what ends the exit; the transitions on the
        // theme buttons inside it are not. Whether the exit is still current is
        // settle()'s call, not this listener's.
        if (event.target === parts.sheet && event.propertyName === 'opacity') {
          settle();
        }
      });

      doc.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          hide(true);
        }
      });

      doc.body.appendChild(element);
    }

    function show(model) {
      if (!element) {
        build();
      }

      cancelTimers();

      // Read before anything below clears it.
      var wasHidden = element.hasAttribute('hidden');

      // A repeat add while the sheet is on its way out takes it back: dropping
      // `is-leaving` lets it animate in again from wherever the exit got to, and
      // the cancelled hide can no longer apply `hidden` because the state below
      // moves off 'leaving'.
      element.classList.remove('is-leaving');
      element.inert = false;

      opener = model.opener;

      parts.title.textContent = model.title;
      parts.title.hidden = model.title === '';
      parts.meta.textContent = model.meta;

      if (model.image) {
        parts.image.setAttribute('src', model.image);
        parts.image.hidden = false;
      } else {
        parts.image.removeAttribute('src');
        parts.image.hidden = true;
      }

      // Keep the actions clear of the theme editor / preview bar.
      element.classList.toggle('eataly-added--preview-bar', previewBarVisible());

      // Clearing the live region and filling it on the next frame is what makes
      // a second add re-announce, even when it is the same line as last time.
      parts.announcement.textContent = '';
      announcement = model.announcement;

      // Going from display:none to displayed inside one frame, the browser is
      // free to coalesce both style changes and skip the entry transition
      // entirely. Reading a layout property forces it to commit the base
      // transform/opacity first, so there is something to animate from.
      //
      // Only on the way up from display:none. A repeat add during the exit is
      // already laid out, and forcing the base state on it would snap the sheet
      // back down to translateY(20px) before it animates in again.
      if (wasHidden) {
        element.removeAttribute('hidden');
        void parts.sheet.offsetHeight;
      }

      state = 'entering';

      // The frame is the paint boundary the entry transition needs; reduced
      // motion is handled in the CSS, which makes the same class change instant.
      win.requestAnimationFrame(function () {
        if (state !== 'entering') {
          return; // dismissed before the first frame ever ran: stay down
        }

        state = 'open';
        element.classList.add('is-visible');

        // Read, not closed over: two adds a frame apart both announce the newer
        // one rather than the older frame re-announcing what it was told.
        parts.announcement.textContent = announcement;

        // The preview bar iframe is injected asynchronously, so re-check it
        // once the sheet is actually on screen.
        element.classList.toggle('eataly-added--preview-bar', previewBarVisible());
      });

      timer = win.setTimeout(hide, AUTO_DISMISS_MS);
    }

    // Capture phase, so the metadata exists before the theme's delegated
    // handler (bubble phase, on the section) issues the add request.
    doc.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-action="add-to-cart"]');

      if (button) {
        snapshot(button);
      }
    }, true);

    win.addEventListener('product:added', function (event) {
      if (forwarding) {
        return; // our own forwarded copy, below
      }

      var detail = event.detail || {};

      var confirm = shouldConfirm({
        pageType: (win.theme || {}).pageType,
        isMobile: win.matchMedia(MOBILE_QUERY).matches,
        quantity: detail.quantity
      });

      if (!confirm) {
        return; // desktop, cart page, or not a product add: leave the theme alone
      }

      var model = describeAdd(detail);

      if (!model) {
        return;
      }

      // Stops the theme's scroll-to-top and legacy drawer, which is the whole point.
      event.stopPropagation();

      // The theme documents `product:added` as an app integration point, so
      // forward an identical event to listeners above document.documentElement.
      forwarding = true;

      try {
        doc.dispatchEvent(new win.CustomEvent('product:added', { bubbles: true, detail: detail }));
      } finally {
        forwarding = false;
      }

      // The theme's own refresh path: header count, mini-cart contents, loading bar.
      doc.documentElement.dispatchEvent(new win.CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { scrollToTop: false }
      }));

      show(model);
    }, true);
  }

  return {
    MOBILE_QUERY: MOBILE_QUERY,
    REDUCED_MOTION_QUERY: REDUCED_MOTION_QUERY,
    EXIT_MS: EXIT_MS,
    EXIT_FALLBACK_MS: EXIT_FALLBACK_MS,
    PREVIEW_BAR_IDS: PREVIEW_BAR_IDS,
    positiveQuantity: positiveQuantity,
    variantLabel: variantLabel,
    thumbnail: thumbnail,
    shouldConfirm: shouldConfirm,
    describeAdd: describeAdd,
    snapshot: snapshot,
    init: init
  };
});
